import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { 
  Card, 
  CardBody, 
  Button, 
  Chip, 
  Progress, 
  Divider,
  Badge,
  Tooltip
} from "@nextui-org/react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { 
  faGripVertical, 
  faTable, 
  faSearch, 
  faRefresh,
  faCalendarAlt,
  faUser,
  faFlag,
  faClock,
  faCheckCircle,
  faExclamationTriangle,
  faInfoCircle
} from "@fortawesome/free-solid-svg-icons";
import BreadCrumbsPage from "../../components/BreadCrumbsPage";
import { getAlldata } from "../../controllers/APIController";
import { getUserRoleFromOrg, timeAgo } from "../../utils/helper";
import { useQuery } from "@tanstack/react-query";



const NotificationsTasks = () => {
  const { _orgId } = useParams();
  const navigate = useNavigate();
  
  // State management
  const [tasks, setTasks] = useState([]);
  const [filteredTasks, setFilteredTasks] = useState([]);
  const [filtering, setFiltering] = useState(false);
  const [layoutMode, setLayoutMode] = useState("cards");
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [priorityFilter, setPriorityFilter] = useState("all");
  const [roleFilter, setRoleFilter] = useState("all");
  const [userRole, setUserRole] = useState("");
  const [userData, setUserData] = useState(null);
  const [showCompleted, setShowCompleted] = useState(false);
  const userLogged = JSON.parse(sessionStorage.getItem("userData"));
  

  // Task statistics
  const [stats, setStats] = useState({
    total: 0,
    byStatus: {},
    byPriority: {},
    byRole: {}
  });


  useEffect(() => {
    applyFilters();
  }, [tasks, searchTerm, statusFilter, priorityFilter, roleFilter, showCompleted]);

  // Debounced search effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchTerm) {
        setFiltering(true);
        applyFilters();
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);



   const { data: role } = useQuery({
     queryKey: ["roles", _orgId],
     queryFn: () => getUserRoleFromOrg(_orgId, userLogged.user.id),
   });
   const { data: taskResponse,loading, refetch} = useQuery({
     queryKey: ["tasks"],
     queryFn: () => getAlldata("tasks"),
   });

   useEffect(() => {
     if (userLogged?.user) {
       setUserData(userLogged.user);
       setUserRole(role);
     }

     fetchTasks();
   }, [taskResponse, role, refetch]);

 

  const fetchTasks = async () => {
    try {
      
      let tasksData = [];
      if (taskResponse && Array.isArray(taskResponse)) {
        tasksData = taskResponse;
      } else if (
        taskResponse &&
        taskResponse.data &&
        Array.isArray(taskResponse.data)
      ) {
        tasksData = taskResponse.data;
      } else if (
        taskResponse &&
        taskResponse.data &&
        Array.isArray(taskResponse.data.data)
      ) {
        tasksData = taskResponse.data.data;
      }

      if (Array.isArray(tasksData)) {
        // Filter tasks for current user based on role
        const userTasks = tasksData.filter(task => {
          if (!task?.attributes) return false;
          
          const taskData = task.attributes;
          const userId = userData?.id;
          
          if (!userId) return false;
          
          // Check if user is assignee
          const isAssignee = taskData.assignedTo?.data?.id === userId || 
                           taskData.assignedTo === userId;
          
          // Check if user is reviewer or approver by examining the metric data
          let isReviewer = false;
          let isApprover = false;
          
          if (taskData.metric?.data?.attributes) {
            const metricData = taskData.metric.data.attributes;
            
            // Check reviewer list
            if (metricData.ReviewerTo?.data) {
              isReviewer = metricData.ReviewerTo.data.some(reviewer => 
                reviewer.id === userId || reviewer.id?.toString() === userId?.toString()
              );
            }
            
            // Check approver list
            if (metricData.ApproverTo?.data) {
              isApprover = metricData.ApproverTo.data.some(approver => 
                approver.id === userId || approver.id?.toString() === userId?.toString()
              );
            }
          }
          
          // Admin can see all tasks
          if (userRole === "Admin") return true;
          
          return isAssignee || isReviewer || isApprover;
        });

        // Add role information to each task
        const tasksWithRoles = userTasks.map(task => {
          const taskData = task.attributes;
          const userId = userData?.id;
          
          let userRoleInTask = "none";
          if (taskData.assignedTo?.data?.id === userId || taskData.assignedTo === userId) {
            userRoleInTask = "assignee";
          } else if (taskData.metric?.data?.attributes?.ReviewerTo?.data?.some(reviewer => 
            reviewer.id === userId || reviewer.id?.toString() === userId?.toString()
          )) {
            userRoleInTask = "reviewer";
          } else if (taskData.metric?.data?.attributes?.ApproverTo?.data?.some(approver => 
            approver.id === userId || approver.id?.toString() === userId?.toString()
          )) {
            userRoleInTask = "approver";
          }
          
          return {
            ...task,
            userRoleInTask
          };
        });

        setTasks(tasksWithRoles);
        calculateStats(tasksWithRoles);
      } else {
        setTasks([]);
        calculateStats([]);
      }
    } catch (error) {
      console.error("Error fetching tasks:", error);
      setTasks([]);
      calculateStats([]);
    } 
  };

  const calculateStats = (tasksData) => {
    const statusCount = {};
    const priorityCount = {};
    const roleCount = {};

    tasksData.forEach(task => {
      const taskData = task.attributes;
      
      // Status counts
      const status = taskData.Status || "unknown";
      statusCount[status] = (statusCount[status] || 0) + 1;
      
      // Priority counts
      const priority = taskData.Priority || "unknown";
      priorityCount[priority] = (priorityCount[priority] || 0) + 1;
      
      // Role counts
      const role = task.userRoleInTask || "none";
      roleCount[role] = (roleCount[role] || 0) + 1;
    });

    setStats({
      total: tasksData.length,
      byStatus: statusCount,
      byPriority: priorityCount,
      byRole: roleCount
    });
  };

  const applyFilters = () => {
    setFiltering(true);
    
    let filtered = tasks;

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(task => {
        const taskData = task.attributes;
        const searchLower = searchTerm.toLowerCase();
        return (
          taskData.Name?.toLowerCase().includes(searchLower) ||
          taskData.Description?.toLowerCase().includes(searchLower)
        );
      });
    }

    // Status filter
    if (statusFilter !== "all") {
      filtered = filtered.filter(task => 
        task.attributes.Status === statusFilter
      );
    }

    // Priority filter
    if (priorityFilter !== "all") {
      filtered = filtered.filter(task => 
        task.attributes.Priority === priorityFilter
      );
    }

    // Role filter
    if (roleFilter !== "all") {
      filtered = filtered.filter(task => 
        task.userRoleInTask === roleFilter
      );
    }

    // Completed tasks filter
    if (!showCompleted) {
      filtered = filtered.filter(task => 
        task.attributes.Status !== "completed"
      );
    }

    setFilteredTasks(filtered);
    
    // Use setTimeout to simulate the filtering process and provide visual feedback
    setTimeout(() => {
      setFiltering(false);
    }, 300);
  };

  const getStatusColor = (status) => {
    const statusColors = {
      "not_started": "default",
      "assigned": "primary",
      "in_progress": "warning",
      "under_review": "secondary",
      "completed": "success",
      "on_hold": "danger"
    };
    return statusColors[status] || "default";
  };

  const getPriorityColor = (priority) => {
    const priorityColors = {
      "low": "success",
      "medium": "warning",
      "high": "danger",
      "critical": "danger"
    };
    return priorityColors[priority] || "default";
  };

  const getRoleColor = (role) => {
    const roleColors = {
      "assignee": "success",
      "reviewer": "primary",
      "approver": "secondary"
    };
    return roleColors[role] || "default";
  };

  const getRoleIcon = (role) => {
    const roleIcons = {
      "assignee": faUser,
      "reviewer": faCheckCircle,
      "approver": faFlag
    };
    return roleIcons[role] || faInfoCircle;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not set";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  /**
   * Constructs the metric creation URL based on category and name
   * @param {string} category - Metric category (e.g., "GHG Emissions")
   * @param {string} name - Metric name (e.g., "Fugitive Emissions")
   * @returns {string} Formatted URL for metric creation
   */
  const constructMetricUrl = (category, name) => {
    if (!category || !name) {
      console.warn('Missing category or name for metric URL construction', { category, name });
      return null;
    }
    
    // Convert to lowercase and replace spaces with hyphens
    const formattedCategory = category.toLowerCase().replace(/\s+/g, '-');
    const formattedName = name.toLowerCase().replace(/\s+/g, '-');
    
    const finalUrl = `/${_orgId}/metrics/${formattedCategory}/${formattedName}/create`;
    return finalUrl;
  };

  const handleTaskAction = (task, action) => {
    const taskData = task.attributes;
    
    switch (action) {
      case 'view':
        // Navigate to the metric creation form if available
        if (taskData.metric?.data?.attributes) {
          const metricAttrs = taskData.metric.data.attributes;
          const metricUrl = constructMetricUrl(metricAttrs.Category, metricAttrs.Name);
          
          if (metricUrl) {
            navigate(metricUrl);
          } else {
            // Show user feedback that navigation failed
            alert('Unable to navigate to metric. Please check the task details.');
          }
        } else {
          // Show user feedback that no metric is linked
          alert('This task is not linked to any metric. Please contact an administrator.');
        }
        break;
      case 'update':
        // Handle task update - could open a modal or navigate to task edit
        break;
      default:
        break;
    }
  };

  const renderTaskCard = (task) => {
    const taskData = task.attributes;
    const isOverdue = new Date(taskData.EstimatedEndDate) < new Date() && 
                      taskData.Status !== "completed";

    const handleCardClick = () => {
      // Navigate to the metric creation form if available
      if (taskData.metric?.data?.attributes) {
        const metricAttrs = taskData.metric.data.attributes;
        const metricUrl = constructMetricUrl(metricAttrs.Category, metricAttrs.Name);
        
        if (metricUrl) {
          navigate(metricUrl);
        } else {
          // Show user feedback that navigation failed
          alert('Unable to navigate to metric. Please check the task details.');
        }
      } else {
        // Show user feedback that no metric is linked
        alert('This task is not linked to any metric. Please contact an administrator.');
      }
    };

    return (
      <Card 
        key={task.id} 
        className={`border-2 transition-all hover:shadow-md hover:border-emerald-300 cursor-pointer ${
          isOverdue ? 'border-red-200 bg-red-50' : 'border-slate-200'
        }`}
        shadow="sm"
        onClick={handleCardClick}
      >
        <CardBody className="p-4">
          {/* Task Header */}
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-2">
                <Chip
                  size="sm"
                  color={getRoleColor(task.userRoleInTask)}
                  variant="flat"
                  startContent={<FontAwesomeIcon icon={getRoleIcon(task.userRoleInTask)} className="w-3 h-3" />}
                >
                  {task.userRoleInTask.charAt(0).toUpperCase() + task.userRoleInTask.slice(1)}
                </Chip>
                <Chip
                  size="sm"
                  color={getStatusColor(taskData.Status)}
                  variant="flat"
                >
                  {taskData.Status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                </Chip>
                <Chip
                  size="sm"
                  color={getPriorityColor(taskData.Priority)}
                  variant="flat"
                >
                  {taskData.Priority?.charAt(0).toUpperCase() + taskData.Priority?.slice(1)}
                </Chip>
              </div>
              
              <h3 className="font-semibold text-slate-900 text-sm mb-1">
                {taskData.Name}
              </h3>
              
              {taskData.Description && (
                <p className="text-xs text-slate-600 line-clamp-2">
                  {taskData.Description}
                </p>
              )}
              
              {/* Linked Metric Info */}
              {taskData.metric?.data?.attributes && (
                <div className="mt-2 p-2 bg-slate-50 rounded text-xs">
                  <span className="text-slate-500">Metric: </span>
                  <span className="font-medium text-slate-700">
                    {taskData.metric.data.attributes.Name || 'Unnamed Metric'}
                  </span>
                </div>
              )}
            </div>
            
            {isOverdue && (
              <Tooltip content="Overdue">
                <FontAwesomeIcon 
                  icon={faExclamationTriangle} 
                  className="text-red-500 text-sm" 
                />
              </Tooltip>
            )}
          </div>

          {/* Progress Bar */}
          <div className="mb-3">
            <div className="flex items-center justify-between text-xs text-slate-600 mb-1">
              <span>Progress</span>
              <span>{taskData.Progress || 0}%</span>
            </div>
            <Progress
              size="sm"
              value={taskData.Progress || 0}
              color="success"
              className="w-full"
            />
          </div>

          {/* Task Details */}
          <div className="space-y-2 text-xs text-slate-600">
            <div className="flex items-center gap-2">
              <FontAwesomeIcon icon={faCalendarAlt} className="w-3 h-3" />
              <span>Due: {formatDate(taskData.EstimatedEndDate)}</span>
            </div>
            
            {taskData.EstimatedHours && (
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} className="w-3 h-3" />
                <span>Est. {taskData.EstimatedHours}h</span>
              </div>
            )}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 mt-3">
            <Button
              size="sm"
              variant="flat"
              color="primary"
              className="flex-1 text-xs"
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click when button is clicked
                handleTaskAction(task, 'view');
              }}
            >
              View Details
            </Button>
            <Button
              size="sm"
              variant="flat"
              color="success"
              className="text-xs"
              onClick={(e) => {
                e.stopPropagation(); // Prevent card click when button is clicked
                handleTaskAction(task, 'update');
              }}
            >
              Update
            </Button>
          </div>
          
          {/* Click hint */}
          <div className="text-xs text-slate-500 text-center mt-2 opacity-60 hover:opacity-100 transition-opacity">
            💡 Click anywhere on the card to open metric
          </div>
        </CardBody>
      </Card>
    );
  };

  const renderTaskTable = () => (
    <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-slate-50 border-b border-slate-200">
            <tr>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Task</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Metric</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Role</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Status</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Priority</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Progress</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Due Date</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-slate-700">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-200">
            {filteredTasks.map((task) => {
              const taskData = task.attributes;
              const isOverdue = new Date(taskData.EstimatedEndDate) < new Date() && 
                                taskData.Status !== "completed";

              return (
                <tr 
                  key={task.id} 
                  className={`${isOverdue ? 'bg-red-50' : 'hover:bg-slate-50'} cursor-pointer transition-colors duration-200`}
                  onClick={() => {
                    // Navigate to the metric creation form if available
                    if (taskData.metric?.data?.attributes) {
                      const metricAttrs = taskData.metric.data.attributes;
                      const metricUrl = constructMetricUrl(metricAttrs.Category, metricAttrs.Name);
                      
                      if (metricUrl) {
                        navigate(metricUrl);
                      } else {
                        // Show user feedback that navigation failed
                        alert('Unable to navigate to metric. Please check the task details.');
                      }
                    } else {
                      // Show user feedback that no metric is linked
                      alert('This task is not linked to any metric. Please contact an administrator.');
                    }
                  }}
                >
                                     <td className="px-4 py-3">
                     <div>
                       <div className="font-medium text-sm text-slate-900">{taskData.Name}</div>
                       {taskData.Description && (
                         <div className="text-xs text-slate-600 truncate max-w-xs">
                           {taskData.Description}
                         </div>
                       )}
                     </div>
                   </td>
                   <td className="px-4 py-3">
                     <div className="text-sm text-slate-900">
                       {taskData.metric?.data?.attributes?.Name || 'No Metric'}
                     </div>
                   </td>
                  <td className="px-4 py-3">
                    <Chip
                      size="sm"
                      color={getRoleColor(task.userRoleInTask)}
                      variant="flat"
                      startContent={<FontAwesomeIcon icon={getRoleIcon(task.userRoleInTask)} className="w-3 h-3" />}
                    >
                      {task.userRoleInTask.charAt(0).toUpperCase() + task.userRoleInTask.slice(1)}
                    </Chip>
                  </td>
                  <td className="px-4 py-3">
                    <Chip
                      size="sm"
                      color={getStatusColor(taskData.Status)}
                      variant="flat"
                    >
                      {taskData.Status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                    </Chip>
                  </td>
                  <td className="px-4 py-3">
                    <Chip
                      size="sm"
                      color={getPriorityColor(taskData.Priority)}
                      variant="flat"
                    >
                      {taskData.Priority?.charAt(0).toUpperCase() + taskData.Priority?.slice(1)}
                    </Chip>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Progress
                        size="sm"
                        value={taskData.Progress || 0}
                        color="success"
                        className="w-16"
                      />
                      <span className="text-xs text-slate-600">{taskData.Progress || 0}%</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="text-sm text-slate-900">
                      {formatDate(taskData.EstimatedEndDate)}
                    </div>
                    {isOverdue && (
                      <div className="text-xs text-red-600">Overdue</div>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        variant="flat" 
                        color="primary" 
                        className="text-xs"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click when button is clicked
                          handleTaskAction(task, 'view');
                        }}
                      >
                        View
                      </Button>
                      <Button 
                        size="sm" 
                        variant="flat" 
                        color="success" 
                        className="text-xs"
                        onClick={(e) => {
                          e.stopPropagation(); // Prevent row click when button is clicked
                          handleTaskAction(task, 'update');
                        }}
                      >
                        Update
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Page Header Skeleton */}
          <div className="bg-gradient-to-r from-white to-slate-50 border-b border-slate-200 mb-6">
            <div className="py-4">
              <div className="h-8 bg-slate-200 rounded-lg w-1/3 mb-2 animate-pulse"></div>
              <div className="h-4 bg-slate-200 rounded-lg w-1/2 animate-pulse"></div>
            </div>
          </div>
          
          {/* Breadcrumbs Skeleton */}
          <div className="mb-6">
            <div className="h-4 bg-slate-200 rounded-lg w-1/4 animate-pulse"></div>
          </div>
          
          {/* Content Loading */}
          <div className="text-center">
            <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-emerald-600 mx-auto mb-6"></div>
            <h3 className="text-xl font-semibold text-slate-900 mb-3">Loading your tasks...</h3>
            <p className="text-slate-600 mb-4">Please wait while we fetch your assigned tasks and notifications.</p>
            <div className="flex justify-center gap-2">
              <div className="h-2 w-2 bg-emerald-600 rounded-full animate-bounce"></div>
              <div className="h-2 w-2 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.1s'}}></div>
              <div className="h-2 w-2 bg-emerald-600 rounded-full animate-bounce" style={{animationDelay: '0.2s'}}></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!userData) {
    return (
      <div className="min-h-screen bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <FontAwesomeIcon icon={faExclamationTriangle} className="w-8 h-8 text-red-600" />
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">User not authenticated</h3>
            <p className="text-slate-600 mb-4">
              Please log in to view your tasks.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-white to-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div>
                <h1 className="text-3xl font-bold text-slate-900">My Tasks & Notifications</h1>
                <p className="text-slate-600 mt-1">Manage your assigned tasks, reviews, and approvals</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Breadcrumbs */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-4 py-2">
        <BreadCrumbsPage />
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
        <div className="flex flex-col gap-6">
          {/* Search and Filter Section */}
          <div className="bg-white rounded-lg border border-slate-200 shadow-sm p-4">
            <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
              <div className="flex-1">
                <div className="relative">
                  <FontAwesomeIcon 
                    icon={faSearch} 
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" 
                  />
                  <input
                    type="text"
                    placeholder="Search tasks by name or description..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                  />
                  {searchTerm && filtering && (
                    <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                    </div>
                  )}
                </div>
              </div>
              
              <div className="flex items-center gap-3">
                {/* Filters */}
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={filtering}
                >
                  <option value="all">All Statuses</option>
                  <option value="not_started">Not Started</option>
                  <option value="assigned">Assigned</option>
                  <option value="in_progress">In Progress</option>
                  <option value="under_review">Under Review</option>
                  <option value="completed">Completed</option>
                  <option value="on_hold">On Hold</option>
                </select>

                <select
                  value={priorityFilter}
                  onChange={(e) => setPriorityFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={filtering}
                >
                  <option value="all">All Priorities</option>
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>

                <select
                  value={roleFilter}
                  onChange={(e) => setRoleFilter(e.target.value)}
                  className="px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                  disabled={filtering}
                >
                  <option value="all">All Roles</option>
                  <option value="assignee">Assignee</option>
                  <option value="reviewer">Reviewer</option>
                  <option value="approver">Approver</option>
                </select>

                {/* View Toggle */}
                <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
                  <Button
                    size="sm"
                    variant={layoutMode === "cards" ? "solid" : "light"}
                    color={layoutMode === "cards" ? "success" : "default"}
                    onClick={() => setLayoutMode("cards")}
                    className="px-3 py-1 text-xs"
                    disabled={filtering}
                  >
                    <FontAwesomeIcon icon={faGripVertical} className="w-3 h-3 mr-1" />
                    Cards
                  </Button>
                  <Button
                    size="sm"
                    variant={layoutMode === "table" ? "solid" : "light"}
                    color={layoutMode === "table" ? "success" : "default"}
                    onClick={() => setLayoutMode("table")}
                    className="px-3 py-1 text-xs"
                    disabled={filtering}
                  >
                    <FontAwesomeIcon icon={faTable} className="w-3 h-3 mr-1" />
                    Table
                  </Button>
                </div>

                {/* Show Completed Toggle */}
                <div className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    id="showCompleted"
                    checked={showCompleted}
                    onChange={(e) => setShowCompleted(e.target.checked)}
                    className="w-4 h-4 text-emerald-600 border-slate-300 rounded focus:ring-emerald-500"
                    disabled={filtering}
                  />
                  <label htmlFor="showCompleted" className="text-sm text-slate-600">
                    Show Completed
                  </label>
                </div>

                {/* Refresh Button */}
                <Button
                  variant="flat"
                  startContent={loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-emerald-600"></div>
                  ) : (
                    <FontAwesomeIcon icon={faRefresh} />
                  )}
                  onClick={fetchTasks}
                  className="bg-white border border-slate-200 text-slate-700 hover:bg-slate-50"
                  disabled={loading}
                >
                  {loading ? "Refreshing..." : "Refresh"}
                </Button>
              </div>
            </div>
          </div>

          {/* Content Display */}
          {loading ? (
            <Card className="border border-slate-200 shadow-sm">
              <CardBody className="p-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Loading your tasks...</h3>
                  <p className="text-slate-600">Please wait while we fetch your assigned tasks and notifications.</p>
                </div>
              </CardBody>
            </Card>
          ) : filtering ? (
            <Card className="border border-slate-200 shadow-sm">
              <CardBody className="p-8">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">Applying filters...</h3>
                  <p className="text-slate-600">Please wait while we filter your tasks.</p>
                </div>
              </CardBody>
            </Card>
          ) : filteredTasks.length === 0 ? (
            <Card className="border border-slate-200 shadow-sm">
              <CardBody className="p-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FontAwesomeIcon icon={faCheckCircle} className="w-8 h-8 text-emerald-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">No tasks found</h3>
                  <p className="text-slate-600 mb-4">
                    {searchTerm || statusFilter !== "all" || priorityFilter !== "all" || roleFilter !== "all" || !showCompleted
                      ? "Try adjusting your filters or search terms."
                      : "You don't have any tasks assigned to you at the moment."}
                  </p>
                  {(searchTerm || statusFilter !== "all" || priorityFilter !== "all" || roleFilter !== "all" || !showCompleted) && (
                    <Button
                      size="sm"
                      variant="flat"
                      color="primary"
                      onClick={() => {
                        setSearchTerm("");
                        setStatusFilter("all");
                        setPriorityFilter("all");
                        setRoleFilter("all");
                        setShowCompleted(false);
                      }}
                    >
                      Clear Filters
                    </Button>
                  )}
                </div>
              </CardBody>
            </Card>
          ) : (
            <>
              {/* Results Summary */}
              <div className="text-sm text-slate-600 mb-4 flex items-center gap-2">
                <span>Showing {filteredTasks.length} of {tasks.length} tasks</span>
                {filtering && (
                  <div className="flex items-center gap-1 text-emerald-600">
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-emerald-600"></div>
                    <span className="text-xs">Filtering...</span>
                  </div>
                )}
              </div>
              
              {layoutMode === "cards" ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {filteredTasks.map(renderTaskCard)}
                </div>
              ) : (
                renderTaskTable()
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default NotificationsTasks;
